import {FC, useEffect, useState, useCallback} from "react";
import {Icon, Segment} from "semantic-ui-react";
import {LoadingMessage, ErrorMessage} from "@/components/SystemMessages";
import {DataStatus} from "@/features/application/api/applicationModels";
import {ICoachInsightsResponse, IInsightAction} from "./api/coachInsightsModels";
import coachInsightsAPI from "./api/coachInsightsAPI";
import insightActionsStore from "./api/insightActionsStore";
import InsightActionRow from "./components/InsightActionRow";
import InsightFollowUpPanel from "./components/InsightFollowUpPanel";
import {ISODateString} from "@/util/dateUtil";

interface IProps {
    businessUnitId: string;
    date: ISODateString;
}

function formatRoiTotal(insights: ICoachInsightsResponse["speedOfServiceInsights"]): string {
    const total = (insights ?? []).reduce((sum, i) => sum + (i.estimatedRoi ?? 0), 0);
    if (total <= 0) return "";
    if (total >= 1000) return `$${(total / 1000).toFixed(1)}k at stake`;
    return `$${total} at stake`;
}

const CoachInsights: FC<IProps> = ({businessUnitId, date}) => {
    const [dataStatus, setDataStatus] = useState<DataStatus>(DataStatus.Loading);
    const [data, setData] = useState<ICoachInsightsResponse | null>(null);
    const [followUpActions, setFollowUpActions] = useState<IInsightAction[]>([]);

    useEffect(() => {
        const abortController = new AbortController();

        const fetchInsights = async () => {
            setDataStatus(DataStatus.Loading);
            try {
                const response = await coachInsightsAPI.getCoachInsights(
                    businessUnitId,
                    date,
                    abortController.signal
                );
                if (response === null) {
                    setDataStatus(DataStatus.Undefined);
                } else {
                    setData(response);
                    setDataStatus(DataStatus.Loaded);
                }
            } catch (error: any) {
                if (error?.name !== "AbortError") {
                    setDataStatus(DataStatus.Failed);
                }
            }
        };

        fetchInsights();

        return () => abortController.abort();
    }, [businessUnitId, date]);

    const refreshFollowUp = useCallback(async () => {
        const actions = await insightActionsStore.listActions();
        setFollowUpActions(actions);
    }, []);

    useEffect(() => {
        refreshFollowUp();
    }, [refreshFollowUp]);

    if (dataStatus === DataStatus.Undefined) {
        return null;
    }

    const renderBrief = () => {
        if (dataStatus === DataStatus.Loading) {
            return <LoadingMessage marginTop={20} />;
        }
        if (dataStatus === DataStatus.Failed || !data) {
            return <ErrorMessage text="Failed to load Coach Insights." />;
        }
        if (data.message) {
            return <div className="brief-no-data">{data.message}</div>;
        }

        const insights = data.speedOfServiceInsights ?? [];
        const roiLabel = formatRoiTotal(insights);

        return (
            <>
                <div className="brief-summary-bar">
                    <span className="brief-count">
                        <Icon name="exclamation circle" />
                        {insights.length} insight{insights.length !== 1 ? "s" : ""}
                    </span>
                    {roiLabel && (
                        <span className="brief-roi-chip">
                            <Icon name="dollar sign" />
                            {roiLabel}
                        </span>
                    )}
                    <span className="brief-target-chip">
                        <Icon name="clock outline" />
                        Target: {data.driveThruTarget}
                    </span>
                </div>

                <div className="action-rows">
                    {insights.map((insight) => (
                        <InsightActionRow
                            key={`${insight.locationPk}-${insight.dayPart}`}
                            insight={insight}
                            onActionRecorded={refreshFollowUp}
                        />
                    ))}
                </div>
            </>
        );
    };

    return (
        <div className="coach-insights-layout">
            <div className="coach-insights-main">
                <Segment className="coach-insights body-segment">
                    <div className="coach-insights-header">
                        <h2>Coach Insights</h2>
                        <span className="beta-badge">Beta Pilot</span>
                    </div>
                    {renderBrief()}
                </Segment>
            </div>
            <div className="coach-insights-sidebar">
                <Segment className="coach-insights body-segment sidebar-segment">
                    <InsightFollowUpPanel
                        actions={followUpActions}
                        onActionsChanged={refreshFollowUp}
                    />
                </Segment>
            </div>
        </div>
    );
};

export default CoachInsights;

